// Helpers for the "type-level" (shared) attribute/reward description loc keys.
//
// Instance localization is uid-keyed and already namespaced
// (e.g. "unitattribute.ambush.0"). The shared per-TYPE description was
// historically keyed by the bare type id ("Ambush.description"), which doesn't
// identify its source and forces two collections that happen to share a type id
// (e.g. "Rush" — both a building attribute and a tech action) to share one
// description.
//
// We're migrating those to a namespaced form derived from the instance uid's
// namespace + the type slug: "unitattribute.ambush.description". During the
// migration both keys can coexist; readers/editors prefer the namespaced key
// when present and fall back to the legacy one (see getTypeDescriptionKey on
// AppContext).
//
// A per-instance "<uid>.description" layer in front of these was tried and
// removed: AddRewardMetadata seeds one for every reward it creates, and has done
// since before anything read them, so the data holds auto-generated placeholders
// that would bury the real text. Stop the seeding and clear those first.

// Slug used by namespaced uids/keys (matches mintNamespacedUid in uid.utils):
// lowercased with whitespace collapsed to '-'.
export function descriptionSlug(typeId: string): string {
    return typeId.trim().toLowerCase().replace(/\s+/g, '-');
}

// Legacy bare key, e.g. "Ambush.description".
export function legacyTypeDescriptionKey(typeId: string): string {
    return `${typeId}.description`;
}

// Namespaced key, e.g. ("unitattribute.ambush.0", "Ambush") ->
// "unitattribute.ambush.description". The namespace is the uid's first segment,
// which also disambiguates a type id shared by two collections (each instance's
// uid prefix differs).
export function namespacedTypeDescriptionKey(uid: string, typeId: string): string {
    // Defensive: legacy/older-branch records can lack a uid even though the type
    // says otherwise. Fall back to an empty namespace rather than throwing.
    const namespace = (uid ?? '').split('.')[0];
    return `${namespace}.${descriptionSlug(typeId)}.description`;
}
